import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const cardBase =
  "rounded-xl p-5 flex flex-col gap-3 border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022] hover:shadow-[0_0_20px_#00ff9044] transition-shadow";

const ratingColor = (r) => {
  if (r >= 4.5) return "#00ff90";
  if (r >= 3.5) return "#60a5fa";
  if (r >= 2.5) return "#fbbf24";
  return "#f87171";
};

const PerformanceSnapshot = ({ managerId }) => {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API_BASE}/api/manager/performance?manager_id=${managerId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => { if (!res.ok) throw new Error("Failed to load performance"); return res.json(); })
      .then((data) => setRatings(Array.isArray(data) ? data : (data.ratings || data?.data || [])))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [managerId]);

  const chartData = ratings.map((r) => ({
    name: r.name?.split(" ")[0] || "?",
    rating: typeof r.rating === "number" ? r.rating : parseFloat(r.rating) || 0,
  }));

  const avgRating = chartData.length > 0
    ? (chartData.reduce((a, b) => a + b.rating, 0) / chartData.length).toFixed(1)
    : null;

  return (
    <div className={cardBase}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest">Performance Snapshot</span>
        <span className="text-2xl">⭐</span>
      </div>

      {loading && <p className="text-white/20 text-sm animate-pulse">Loading...</p>}
      {error && <p className="text-red-400 text-sm">⚠ {error}</p>}

      {!loading && !error && (
        <>
          {avgRating && (
            <div className="text-2xl font-bold text-white">
              {avgRating}<span className="text-base font-normal text-white/30">/5 avg</span>
            </div>
          )}

          {chartData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={Math.max(80, chartData.length * 28)}>
                <BarChart layout="vertical" data={chartData} margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                  <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 10, fill: "rgba(255,255,255,0.25)" }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }} width={55} />
                  <Tooltip
                    contentStyle={{ background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 }}
                    labelStyle={{ color: "#00ff90" }}
                    formatter={(val) => [`${val} / 5`, "Rating"]}
                  />
                  <Bar dataKey="rating" radius={[0, 4, 4, 0]}>
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={ratingColor(entry.rating)} fillOpacity={0.85} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              <div className="flex gap-3 text-xs text-white/25 flex-wrap">
                <span><span className="text-[#00ff90]">●</span> Excellent (4.5+)</span>
                <span><span className="text-blue-400">●</span> Good (3.5+)</span>
                <span><span className="text-yellow-400">●</span> Fair (2.5+)</span>
                <span><span className="text-red-400">●</span> Needs attention</span>
              </div>
            </>
          ) : (
            <p className="text-white/20 text-sm">No performance data.</p>
          )}
        </>
      )}
    </div>
  );
};

export default PerformanceSnapshot;